import {SelectionMode} from 'components/FluentUICompat';
import React, {useEffect, useMemo, useState} from 'react';
import {Requirement} from 'types';
import {
	funcDecodeHex24ToDocId,
	InfinityList,
	InfinityListItemWithId,
	InfinityListView,
} from 'components/InfinityList';

import {useTranslation} from 'react-i18next';
import {
	useGetRequirementsNextLazyQuery,
	useGetRequirementsQueryLazyQuery,
	useGetRequirementsSortOrderLazyQuery,
} from './hooks/requirementsInfinity.generated';
import {useNavigate} from 'react-router-dom';
import {useRequirementsTranslation} from './hooks/useRequirementsTranslation';
import {useUserContext} from 'authentication/UserContext';
import {EntityContextProvider} from 'components/EntityPage/EntityContext';
import {RequirementsTooltipTranslationProvider} from './RequirementsTooltipTranslationProvider';
import {RequirementsPageRequirementForm} from './components/RequirementsPageRequirementForm';
import {
	addFavoriteView,
	useInfinityListStandardHooks,
} from 'components/InfinityList/InfinitListStandardHooks';
import {
	useRequirementColumnMetaData,
	useRequirementsPageColumn,
	useRequirementsPageFilterOptions,
} from './RequirementsPageColumns';
import {handleClickRequirements} from 'components/InfinityList/Excel/XLRequirementsPage';
import {FaqPanel} from 'components/Faq/FaqPanel';
import {useGetAllKeywordsQuery} from 'features/AdminSection/hooks/keywords.generated';
import {useGetAllMarketsQuery} from 'features/AdminSection/hooks/markets.generated';
import {useQuestionCountsByEntityTypeLazyQuery} from 'components/Faq/Faq.generated';
import {isDevEnvironment, isTestEnvironment} from 'appShell/Layout/Navigation';
import {AggregateRequirementsButton} from './components/AggregateButtons';

const initialSortField = 'name';
const initialSortOrder = 'ASC';
const intBatchSize = 50;

const RequirementsPage: React.FC = () => {
	const {t} = useRequirementsTranslation();

	const {t: tViews} = useTranslation('features/requirements', {
		keyPrefix: 'Views',
	});

	const {t: tFav} = useTranslation('appShell/sidenavigation', {
		keyPrefix: 'SideNavigation',
	});

	const {t: tExcel} = useTranslation();

	const {
		isAdmin,
		isVko,
		isVex,
		isVexSupport,
		isReader,
		isBedarfskoordinator,
		myVexClusters,
		favoriteIds,
	} = useUserContext();

	const arrRoles = {
		isAdmin,
		isVko,
		isVex,
		isVexSupport,
		isReader,
		isBedarfskoordinator,
	};

	const {data: keywordData} = useGetAllKeywordsQuery();
	const {data: marketData} = useGetAllMarketsQuery();

	const [getSortOrder] = useGetRequirementsSortOrderLazyQuery();

	const [getRequirementsQuery, {loading: loadingQuery, data: dataQuery}] =
		useGetRequirementsQueryLazyQuery();

	const [getRequirementsNext, {loading: loadingDocuments}] =
		useGetRequirementsNextLazyQuery();

	const navigate = useNavigate();

	const views: InfinityListView[] = useMemo(() => {
		const viewsTemp = [
			{
				headerText: tViews('STD'),
				accessKey: 'STD',
				query: isAdmin ? '{}' : `{status:2}`,
			},
		];
		if (myVexClusters.length > 0) {
			const myVexClusterIds = myVexClusters.map(
				x => `'${funcDecodeHex24ToDocId(x.id)}'`,
			);
			viewsTemp.push({
				headerText: tViews('MYVC'),
				accessKey: 'MYVC',
				query: `{'vexClusterRefs._id':{$in:[${myVexClusterIds.join(
					',',
				)}]}, status: {$nin: [4,5]} }`,
			});
		}
		if (isAdmin || isBedarfskoordinator) {
			viewsTemp.push({
				headerText: tViews('INTERIM'),
				accessKey: 'INTERIM',
				query: `{status: {$in: [4,5]}}`,
			});
		}
		addFavoriteView(viewsTemp, favoriteIds, 'Requirement', tFav);

		return viewsTemp;
	}, [isAdmin, isBedarfskoordinator, myVexClusters, favoriteIds]);

	const initialView = 'STD';

	const arrIds = useMemo(
		() => dataQuery?.requirementsQuery.requirementIds ?? [],
		[dataQuery],
	);

	const columns = useRequirementsPageColumn();
	const columnMetaData = useRequirementColumnMetaData();
	const columnFilterOptions = useRequirementsPageFilterOptions(
		dataQuery?.requirementsQuery.requirementFilterOptions,
		keywordData,
		marketData,
	);

	const queryDocumentsDB = async (
		query: string,
		sortField: string,
		sortOrder: string,
	) => {
		const {data: dataQuery} = await getRequirementsQuery({
			variables: {
				queryArgs: {query, sortField, sortOrder},
			},
		});
		const arrDocumentNext = (dataQuery?.requirementsQuery.requirementsBatch ??
			[]) as Requirement[];
		const arrIDsNew = dataQuery?.requirementsQuery.requirementIds ?? [];
		return {arrDocumentNext, arrIDsNew};
	};
	const getDocumentsNextDB = async (preFilteredIds: string[]) => {
		const {data: dataDocumentsNext} = await getRequirementsNext({
			variables: {nextArgs: {preFilteredIds}},
		});

		return (dataDocumentsNext?.requirementsNext ?? []) as Requirement[];
	};

	const getSortOrderDB = async (
		preFilteredIds: string[],
		sortField: string,
	) => {
		const {data: dataSortOrder} = await getSortOrder({
			variables: {
				sortArgs: {sortField, preFilteredIds},
			},
		});

		return dataSortOrder?.requirementsSortOrder ?? [];
	};

	const handleExcelExport = async (arrDocuments: Requirement[]) => {
		const arrDocumentsUpdated = await applyFaqQuestionCounter(arrDocuments);
		await handleClickRequirements(
			arrDocumentsUpdated,
			columns.filter(x => x.hiddenAlways === undefined).map(x => x.name),
			arrRoles,
			myVexClusters,
			tExcel,
		).catch(error => {
			// eslint-disable-next-line no-console
			console.error('Error exporting requirements to Excel:', error);
		});
	};

	const [fetchQuestionCountsByEntity] = useQuestionCountsByEntityTypeLazyQuery({
		variables: {entityType: 'requirements', parentId: null},
	});

	const [questionData, setQuestionData] = useState<any[]>([]);

	const applyFaqQuestionCounter = async (
		items: Requirement[],
	): Promise<Requirement[]> => {
		if (!(isDevEnvironment() || isTestEnvironment())) {
			return items;
		}
		let questionDataTmp = questionData;
		if (questionDataTmp.length === 0) {
			const {data: fetchData} = await fetchQuestionCountsByEntity();
			questionDataTmp = fetchData?.questionCountsByEntityType || [];
			setQuestionData(questionDataTmp);
		}

		const questionByEntityId = new Map(
			(questionDataTmp ?? []).map((q: any) => [q.entityId, q]),
		);

		return (
			items?.map((elem: Requirement) => ({
				...elem,
				question: questionByEntityId.get(elem.id),
			})) ?? []
		);
	};

	const {
		arrDocument,
		keyPrefix,
		linksFilters,
		firstRenderSTD,
		applyFilterSTD,
		applySortSTD,
		loadItemsNextSTD,
		excelExportSTD,
	} = useInfinityListStandardHooks({
		views,
		intBatchSize,
		initialSortField,
		initialSortOrder,
		initialView,
		arrIds,
		columns,
		columnMetaData,
		queryDocumentsDB,
		getDocumentsNextDB,
		getSortOrderDB,
		handleExcelExport,
		applyFaqQuestionCounter,
	});

	const handleViewingItem = async (item: Requirement) => {
		await navigate({
			pathname: `${item.id}`,
		});
	};

	const createHref = (item: unknown) =>
		`/requirements/${(item as InfinityListItemWithId).id}`;

	const numberOfElements = arrIds.length ?? 1;

	return (
		<EntityContextProvider>
			<RequirementsTooltipTranslationProvider>
				<InfinityList
					pageName={t('RequirementsHeader')}
					items={arrDocument}
					sticky={true}
					columns={columns}
					columnMetaData={columnMetaData}
					columnFilterOptions={columnFilterOptions}
					selectionMode={SelectionMode.multiple}
					stillLoading={loadingQuery || loadingDocuments}
					initalFilters={linksFilters}
					initialSortField={initialSortField}
					initialSortOrder={initialSortOrder}
					initialView={initialView}
					keyPrefix={keyPrefix}
					firstRender={firstRenderSTD}
					loadItemsNext={loadItemsNextSTD}
					applyFilter={applyFilterSTD}
					applySort={applySortSTD}
					excelExport={excelExportSTD}
					handleInvokeItem={handleViewingItem}
					createHref={createHref}
					numberOfElements={numberOfElements}
					views={views}
				/>
				<FaqPanel />
				<RequirementsPageRequirementForm />
				<AggregateRequirementsButton />
			</RequirementsTooltipTranslationProvider>
		</EntityContextProvider>
	);
};

export default RequirementsPage;
